/** Image upload to Supabase Storage with automatic WebP conversion and optimization */

import { supabase } from '@mondivago/supabase';

// ─── Image optimization (canvas-based WebP conversion) ───

/** Resizes a user-uploaded image and converts it to WebP. Returns a Blob. */
export async function convertToWebP(file: File, quality = 0.8, maxDimension = 1920): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let width = img.width;
        let height = img.height;

        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = Math.round((height * maxDimension) / width);
            width = maxDimension;
          } else {
            width = Math.round((width * maxDimension) / height);
            height = maxDimension;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Canvas to Blob conversion failed'));
            }
          },
          'image/webp',
          quality
        );
      };

      img.onerror = () => {
        reject(new Error('Failed to load image for conversion'));
      };
      img.src = e.target?.result as string;
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    reader.readAsDataURL(file);
  });
}

function blobToFile(blob: Blob, originalName: string): File {
  const newName = originalName.replace(/\.[^/.]+$/, '') + '.webp';
  return new File([blob], newName, { type: 'image/webp' });
}

// ─── Upload ───

interface UploadOptimizedImageParams {
  file: File;
  bucket: 'activities' | 'users' | 'site_content' | 'content';
  folder: string;
  prefix: string;
}

export async function uploadOptimizedImage({ file, bucket, folder, prefix }: UploadOptimizedImageParams) {
  let fileToUpload: File = file;

  if (file.type.startsWith('image/')) {
    try {
      const optimizedBlob = await convertToWebP(file);
      fileToUpload = blobToFile(optimizedBlob, file.name);
    } catch {
      fileToUpload = file;
    }
  }

  const extension = fileToUpload.name.split('.').pop() ?? (fileToUpload.type === 'image/webp' ? 'webp' : 'jpg');
  const safePrefix = prefix.replace(/[^a-z0-9_-]/gi, '-').toLowerCase();
  const fileName = `${safePrefix}-${String(Date.now())}-${crypto.randomUUID()}.${extension}`;
  const path = `${folder}/${fileName}`;

  const { error } = await supabase.storage.from(bucket).upload(path, fileToUpload, {
    contentType: fileToUpload.type,
    cacheControl: '31536000',
    upsert: false,
  });

  if (error) {
    const reason = error.message.toLowerCase();
    if (reason.includes('bucket') && reason.includes('not found')) {
      throw new Error('An unexpected error occurred while uploading the image. Please try again.');
    }
    if (reason.includes('row-level security') || reason.includes('permission')) {
      throw new Error('You do not have permission to upload images. Please try again.');
    }
    throw new Error('Unable to upload the image. Please try again.');
  }

  const { data } = supabase.storage.from(bucket).getPublicUrl(path);
  return data.publicUrl;
}
